import React, { useContext, useEffect, useState } from "react";
import { StyledTransactionsTable } from "../../Wallets/components/TransactionsTable/style";
import EmptyMobileCard from "../../../components/emptyStates/EmptyMobileCard";
import EmptyVirtualAccount from "../../Customers/components/EmptyVirtualAccount";
import DrawerContainer from "../../../components/common/Drawer/Drawer";
import SenderDetails from "./SenderDetails";
import Loader from "../../../components/common/Loader";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import TableTools from "../../../components/common/TableTools";
import { getVirtualAccountTransactionsData } from "../../../app/queries/virtualAccounts";
import dayjs from "dayjs";
import MobileCard from "./MobileCard";
import { exportVirtualAccountData } from "../utils";
import { formatCurrencyAmount } from "../../../utils/currency";
import { formatLongString } from "../../../utils/formatString";
import { NetworkErrorAlertContext } from "../../../context/NetworkErrorAlert";
import { allWallets } from "../../Wallets/redux/slice";
import { useWindowSize } from "@reach/window-size";
import { selectUserRoleAndPermissions } from "../../Settings/Team/redux/slice";
import CustomTable from "../../../components/common/CustomTable";
import TransactionDetails from "./TransactionDetails";
import { vaCurrencySelectOptions } from "../../Wallets/utils";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FEATURE_FLAG_KEYS } from "../../../utils/featureFlags";

type TransactionPayload = {
    business: string | null;
    currency: string | null;
    pageNumber: number;
    pageSize: number;
    startDay: string | null;
    endDay: string | null;
}

type TableProps = {
    transactions?: any;
    error?: any;
    isLoading?: boolean;
    currencyOfInterest?: string;
}

export default function VirtualAccountTransactionTable({ currencyOfInterest }: TableProps
) {

    const tableColumns = [
        {
            title: "Transaction ID",
            dataIndex: "transaction_id",
            key: "transaction_id",
        },
        {
            title: "Virtual account number",
            dataIndex: "account_number",
            key: "account_number",
        },
        ...(!currencyOfInterest ? [{
            title: "Currency",
            dataIndex: "currency",
            key: "currency",
        }] : []),
        {
            title: "Amount received",
            dataIndex: "amount_received",
            key: "amount_received",
        },
        {
            title: "Amount settled",
            dataIndex: "amount_settled",
            key: "amount_settled",
        },


        {
            title: "Date",
            dataIndex: "date",
            key: "date",
        },
    ]

    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [dateRange, setDateRange] = useState([]);
    const { role, permissions } = useAppSelector(
        selectUserRoleAndPermissions,
    );
    const dispatch = useAppDispatch();
    const { width } = useWindowSize();
    const [currentCurrency, setCurrentCurrency] = useState<string | null>(null)

    const [transactionDetail, setTransactionDetail] =
        useState(null);
    const { businessId, userId } = useAppSelector(
        (state) => state.users,
    );
    const { business } = useAppSelector((state) => state.settings);

    const hasDownloadPermission =
        permissions?.["virtual-account"]?.includes("EXPORT_VA_TRANSACTION");


    useEffect(() => {
        if (businessId && userId) {
            dispatch(allWallets({ businessId }));
        }
    }, [businessId, userId]);

    const { onShowAlert: onShowErrorAlert } = useContext(
        NetworkErrorAlertContext
    );

    const showDrawer = () => {
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
    };

    const handleRowClick = (row: any) => {
        setTransactionDetail(row);
        showDrawer();
    };

    const [payload, setPayload] = useState<TransactionPayload>({
        business: businessId,
        currency: currencyOfInterest || null,
        pageNumber: 1,
        pageSize: 10,
        startDay: null,
        endDay: null,
    });

    const { pageNumber, pageSize } = payload;

    const handleCurrencyChange = (value) => {
        setCurrentCurrency(value);
    }

    const handleFilterProcess = () => {
        setPayload({
            ...payload,
            pageNumber: 1,
            currency: currentCurrency || currencyOfInterest || null,
            startDay: dateRange[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : null,
            endDay: dateRange[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : null,
        })
    }

    const handleResetProcess = () => {
        setPayload({
            ...payload,
            pageNumber: 1,
            currency: currencyOfInterest ? currencyOfInterest : null,
            startDay: null,
            endDay: null,
        })
    }

    const handleDateChange = (dates) => {
        setDateRange(dates);
    }

    const onPageChange = (page: number) => {
        setPayload({
            ...payload,
            pageNumber: page,
        })
    };

    const { data: transactions, isLoading } = getVirtualAccountTransactionsData(payload)

    const tableData =
        transactions?.virtualAccountCollections?.map(
            (item) => ({
                key: item.transaction.id,
                no: item.id,
                transaction_id: formatLongString(item.transaction.tnxRef, 10),
                account_number:
                    item.virtualAccountNumber,
                currency: item.transaction.currency,
                amount_received:
                    formatCurrencyAmount(
                        item.transaction
                            .currency,
                        item.transaction
                            .amount / 100,
                    ),
                amount_settled:
                    formatCurrencyAmount(
                        item.transaction
                            .currency,
                        item.transaction
                            .settledAmount,
                    ),
                date: dayjs(
                    item.transaction.createdAt,
                )?.format("DD.MM.YYYY HH:mm"),
                bank_name: item.bankName,
                account_name:
                    item.originatorAccountName,
                origin_account:
                    item.originatorAccountNumber,
                not_formated_amount:
                    item.transaction.amount /
                    100,
                not_formatted_settled:
                    item.transaction
                        .settledAmount,
            }),
        );

    const exportToXLS = async () => {
        if (hasDownloadPermission || role === "OWNER") {
            await exportVirtualAccountData(transactions, "xls");
        }
        else {
            onShowErrorAlert("", "Access is denied")

        }

    };

    const exportToCSV = async () => {
        if (hasDownloadPermission || role === "OWNER") {
            await exportVirtualAccountData(transactions, "csv");
        } else {
            onShowErrorAlert("", "Access is denied")


        }
    };

    const filteredTransactions = tableData?.filter((item) =>
        Object.values(item).some(
            (val) =>
                typeof val === "string" &&
                val.toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const totalAmount =
        transactionDetail?.not_formated_amount ?? 0;

    const enableVirtualAccountsV2 = useFeatureFlagEnabled(FEATURE_FLAG_KEYS.virtualAccountsV2);

    return (
        <>
            <StyledTransactionsTable>
                <h3>Transactions</h3>
                {business?.approved && tableData && (
                    <TableTools
                        onSearchChange={(e) =>
                            setSearchQuery(e.target.value)
                        }
                        placeHolder="Search by account number"
                        onDateChange={handleDateChange}
                        onFilterButtonClick={handleFilterProcess}
                        onResetButtonClick={handleResetProcess}
                        onXLSButtonClick={exportToXLS}
                        onCSVButtonClick={exportToCSV}
                        showSelectOptions={enableVirtualAccountsV2 && !currencyOfInterest}
                        selectOptions={vaCurrencySelectOptions}
                        selectTitle="Currency"
                        onStatusChange={handleCurrencyChange}
                    />
                )}

                {width > 767 ? (
                    <CustomTable
                        columns={tableColumns}
                        dataSource={filteredTransactions}
                        handleRowClick={handleRowClick}
                        currentPage={pageNumber}
                        pageSize={pageSize}
                        totalItemCount={
                            transactions?.totalCount || 0
                        }
                        onPageChange={onPageChange}
                        extraEmptyChildren={
                            <EmptyVirtualAccount
                                title="No transactions yet"
                                paragraph="All received transactions will be shown here."
                            />
                        }
                    />
                ) : (
                    <>
                        {tableData?.length === 0 ? (
                            <EmptyMobileCard
                                title="No transactions yet"
                                subText="All received or sent transactions will appear here"
                            />
                        ) : (
                            tableData?.map((data) => (
                                <MobileCard
                                    key={data.id}
                                    transactionRef={formatLongString(
                                        data.transaction_id,
                                        10,
                                    )}
                                    accountNumber={
                                        data.account_number
                                    }
                                    amountReceived={
                                        data.amount_received
                                    }
                                    amountSettled={
                                        data.amount_settled
                                    }
                                    date={data.date}
                                    onClick={() =>
                                        handleRowClick(data)
                                    }
                                />
                            ))
                        )}
                    </>
                )}
            </StyledTransactionsTable>

            <DrawerContainer
                title="Transaction details"
                onClose={onClose}
                open={open}
                placement={"right"}
            >
                <TransactionDetails
                    balance={formatCurrencyAmount(
                        transactionDetail?.currency,
                        totalAmount,
                    )}
                    transaction_id={
                        transactionDetail?.transaction_id
                    }
                    virtual_account={
                        transactionDetail?.account_number
                    }
                    amount_received={
                        transactionDetail?.amount_received
                    }
                    amount_settled={
                        transactionDetail?.amount_settled
                    }
                    date={transactionDetail?.date}
                    transaction_ref={transactionDetail?.ref}
                />
                <SenderDetails
                    account_name={
                        transactionDetail?.account_name
                    }
                    account_number={
                        transactionDetail?.origin_account
                    }
                    bank_name={transactionDetail?.bank_name}

                />
            </DrawerContainer>

            <Loader isLoading={isLoading} />

        </>
    )
}